import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, RouterOutlet, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import translationsIT from '../../public/i18n/it.json';
import translationsEN from '../../public/i18n/en.json';
import translationsES from '../../public/i18n/es.json';
import { Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { ConnectionService } from './service/connection.service';
import { ThemeService } from './service/theme.service';
import { CHANGELOG } from './pages/novita/novita.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, CommonModule, TranslatePipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit {
  title = 'nyzed-front';
  latestChangelog = CHANGELOG[0];

  private translate = inject(TranslateService);
  private router = inject(Router);
  private connection = inject(ConnectionService);
  private themeService = inject(ThemeService);

  constructor() {
    this.translate.addLangs(['it', 'en', 'es']);
    const savedLang = localStorage.getItem('lang') as 'it' | 'en' | 'es' | null;
    this.translate.use(savedLang ?? 'it');
    this.translate.setTranslation('it', translationsIT);
    this.translate.setTranslation('en', translationsEN);
    this.translate.setTranslation('es', translationsES);
    this.translate.setFallbackLang('it');
  }
  showChangelog = false;

  ngOnInit() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        this.themeService.syncWithRoute(e.urlAfterRedirects);

        // changelog solo su /home
        if (e.urlAfterRedirects === '/home') {
          this.checkChangelog();
        }
      });

    // crisp — solo per utenti loggati, mai per visitatori anonimi
    const token = localStorage.getItem('token');
    if (token && !environment.landingOnly) {
      this.connection.get(
        '@auth/profile',
        (profile: any) => {
          this.loadCrisp(() => {
            this.setCrispUser(
              `${profile.user?.name ?? ''} ${profile.user?.lastname ?? ''}`.trim(),
              profile.user?.email ?? '',
            );
          });
          this.themeService.reconcileFromProfile(profile.theme);
        },
        () => {},
      );
    }
  }

  private loadCrisp(onReady: () => void) {
    if (typeof window === 'undefined') return;
    if ((window as any).$crisp) {
      onReady();
      return;
    }
    (window as any).CRISP_WEBSITE_ID = '0b8ba473-9099-47de-b7a1-3be5b8101fda';
    (window as any).$crisp = [];
    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    script.onload = onReady;
    document.head.appendChild(script);
  }

  checkChangelog() {
    const token = localStorage.getItem('token');
    if (!token) return;

    const currentVersion = environment.appVersion;
    const seenVersion = localStorage.getItem('changelog_version');
    if (seenVersion === currentVersion) return;

    // primo accesso: segna come visto senza mostrarlo
    if (localStorage.getItem('onboarding_completed') !== 'true') {
      localStorage.setItem('changelog_version', currentVersion);
      return;
    }

    setTimeout(() => (this.showChangelog = true), 1000);
  }

  closeChangelog() {
    localStorage.setItem('changelog_version', environment.appVersion);
    this.connection.put('@auth/preferences', { changelog_version: environment.appVersion }, () => {}, () => {});
    this.showChangelog = false;
  }

  get appVersion(): string {
    return environment.appVersion;
  }

  setCrispUser(name: string, email: string) {
    if (typeof window !== 'undefined' && (window as any).$crisp) {
      (window as any).$crisp.push(['set', 'user:email', [email]]);
      (window as any).$crisp.push(['set', 'user:nickname', [name]]);
      // Il bottone fluttuante di Crisp dà fastidio su mobile (si sovrappone alla
      // bottom tab bar): resta caricato e pronto in background, ma si apre solo
      // da Impostazioni, mai in automatico. Su desktop resta come prima.
      if (window.innerWidth > 768) {
        (window as any).$crisp.push(['do', 'chat:show']);
      } else {
        (window as any).$crisp.push(['do', 'chat:hide']);
      }
    }
  }
}
